/**
 * sessionStorage record of the visitor's demo event, so a reload within the
 * TTL resumes it instead of creating another. Kept apart from the hero chunk:
 * the landing page reads it eagerly to decide whether to reopen the live hero.
 */

export const DEMO_SESSION_KEY = 'sb.demoEvent.v2'

export type StoredDemo = {
  id: string
  eventId: string
  expiresAt: string
  statusUrl: string
  viewerTokenEndpoint: string | null
  /** Proves this browser created the demo; sent as X-Demo-Owner-Token on the poll and on adding cameras. */
  ownerToken: string | null
  inviteUrl: string | null
  cameraName: string | null
}

export function readDemoSession(now = Date.now()): StoredDemo | null {
  try {
    const raw = window.sessionStorage.getItem(DEMO_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredDemo>
    if (
      typeof parsed.id !== 'string' ||
      typeof parsed.eventId !== 'string' ||
      typeof parsed.expiresAt !== 'string' ||
      typeof parsed.statusUrl !== 'string'
    )
      return null
    const expiresAt = Date.parse(parsed.expiresAt)
    if (!Number.isFinite(expiresAt) || expiresAt <= now) {
      writeDemoSession(null)
      return null
    }
    return {
      id: parsed.id,
      eventId: parsed.eventId,
      expiresAt: parsed.expiresAt,
      statusUrl: parsed.statusUrl,
      viewerTokenEndpoint: typeof parsed.viewerTokenEndpoint === 'string' ? parsed.viewerTokenEndpoint : null,
      ownerToken: typeof parsed.ownerToken === 'string' ? parsed.ownerToken : null,
      inviteUrl: typeof parsed.inviteUrl === 'string' ? parsed.inviteUrl : null,
      cameraName: typeof parsed.cameraName === 'string' ? parsed.cameraName : null,
    }
  } catch {
    return null
  }
}

export function writeDemoSession(value: StoredDemo | null) {
  try {
    if (value) window.sessionStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(value))
    else window.sessionStorage.removeItem(DEMO_SESSION_KEY)
  } catch {
    // Private browsing can block storage; the demo still works without resume.
  }
}

/** Keep the stored expiry in step with the server so a reload resumes rather than creating another demo. */
export function updateDemoSessionExpiry(id: string, expiresAt: string) {
  const stored = readDemoSession()
  if (!stored || stored.id !== id || stored.expiresAt === expiresAt) return
  writeDemoSession({ ...stored, expiresAt })
}

export function hasResumableDemo() {
  return readDemoSession() !== null
}
